<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Slider, Alert, TemperatureWheel, Input } from '@Shelly/shelly-design-system';
	// Helpers
	import { isBluTRVCalibrated } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { bluTrvDefaultValue } from 'src/helpers/actions/local/devices/trv_local_actions';
	import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { celsius_to_fahrenheit } from 'src/helpers/interface_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	// Components
	import BlutrvLocalActionAlerts from './BLUTRVLocalActionAlerts.svelte';
	//Types
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';

	export let controlOption: LocalControlOptions, device: Device, errorInput: boolean;

	const unit = device?.ht_ui?.temperature_unit || 'C';
	const valueRange = [
		unit === 'C' ? 4 : Math.round(celsius_to_fahrenheit(4)),
		unit === 'C' ? 30 : Math.round(celsius_to_fahrenheit(30)),
	];
	const btnStep = 0.1;
	const step = unit === 'C' ? btnStep : 1;

	$: errorInput = minMaxInfo?.error !== '' || String(controlOption?.params?.duration) === '';

	$: minMaxInfo = getErrorOrInfoMessageForMinAndMax(
		controlOption.params.duration,
		0,
		35791394,
		false,
		true,
	);

	if (isNaN(controlOption.params.target_C)) {
		if (unit === 'F') {
			controlOption.params.target_C = Math.round(
				celsius_to_fahrenheit(bluTrvDefaultValue('target')),
			);
		} else {
			controlOption.params.target_C = bluTrvDefaultValue('target');
		}
	} else {
		if (unit === 'F') {
			controlOption.params.target_C = Math.round(
				celsius_to_fahrenheit(controlOption.params.target_C),
			);
		}
	}

	let pos = controlOption.params.hasOwnProperty('pos') ? controlOption.params.pos : 50;
	const errorNotCalibrated = {
		show: !isBluTRVCalibrated(device),
		label: $format('alert_device_not_calibrated', {
			default: 'Your device is not calibrated',
		}),
	};

	const a11yProps = {
		sliderAriaLabel: $format('select_valve_target_position', {
			default: 'Select valve target position',
		}),
		ariaLabel: $format('select_valve_target_position', {
			default: 'Select valve target position',
		}),
		ariaLabelValue: $format('select_valve_target_position', {
			values: { value: pos },
			default: 'Select valve target position is ${value}',
		}),
		tempWheelAriaLabel: $format('label_temperature', {
			default: 'Temperature',
		}),
		thumbAriaLabel: $format('label_temperature_thumb', {
			default: 'Temperature thumb',
		}),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

<div>
	{#if controlOption.attr?.pos != undefined}
		<Text class="mb-s" element="h2">
			{$format('label_valve_target_position', {
				default: 'Valve target position',
			})}
		</Text>
		{#if errorNotCalibrated.show}
			<Alert
				class="mb-s"
				heading={errorNotCalibrated.label}
				type="danger"
				cta={{
					title: $format('button_calibrate', {
						default: 'Calibrate',
					}),
				}}
				on:cta_click={() => navigateToSetting(device.id, 'settings', 'calibration')}
			/>
		{:else}
			<BlutrvLocalActionAlerts class="mb-s" {device} action="position" />
		{/if}
		<Slider
			id="slider-lzsuxnw"
			{a11yProps}
			type="simple"
			value={pos}
			class="flex-1"
			disabled={errorNotCalibrated.show}
			on:value_change={(e) => {
				pos = e.detail.value;
				controlOption.params.pos = e.detail.value;
			}}
			title={$format('label_cover_slider_title', {
				values: { pos },
				default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
			})}
		/>
	{:else if controlOption.attr?.duration != undefined}
		<Text class="mb-s" element="h2">
			{$format('text_trv_setting_duration', {
				default: 'Duration',
			})}
		</Text>
		<Input
			size="m"
			value={controlOption.params.duration.toString()}
			label={$format('text_trv_setting_duration', {
				default: 'Duration',
			})}
			{.../^number/.test(controlOption.value) ? { ...getNumberInputConfig() } : {}}
			icon="fa fa-timer"
			tabIndex="-1"
			on:input={(e) => {
				controlOption.params.duration = e.target['value'].trim();
			}}
			info={minMaxInfo.info}
			error={minMaxInfo.error}
			id="input-vcpss3f"
			aria-labelledby="input-vcpss3f"
		/>
	{:else if controlOption.attr?.target_C != undefined}
		<BlutrvLocalActionAlerts class="mb-l" {device} action="target" />
		<TemperatureWheel
			{a11yProps}
			class="mb-s mt-m"
			valuePrefix={`°${unit}`}
			bind:value={controlOption.params.target_C}
			min={valueRange[0]}
			max={valueRange[1]}
			type={'temp'}
			{step}
			{btnStep}
			disabled={!controlOption.attr.target_C}
		/>
	{/if}
</div>
